import { type PowerSyncDrizzleDB, user } from "@fieldcheck/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * ── Why ALL mutations below carry `.where(eq(user.id, userId))` ─
 *
 * Four of them (`locale`, `global_theme_id`, `is_blur`,
 * `follow_device_theme`) had this shape:
 *
 *     const query = db.update(user).set({ … });
 *     await (userId === VISITOR_ID ? query : query.where(eq(user.id, userId)));
 *
 * meaning that, in guest mode, the UPDATE went out **without `WHERE`** → it rewrote ALL
 * `user` rows in the local database.
 *
 * The premise behind it ("the guest database has only one
 * `user` row") is false in a specific window: during the guest→real upgrade, the
 * guest database CONNECTS to the real account's connector and
 * self-subscribes to all streams—including `organizations`, which syncs
 * one `user` row for EACH co-member of that account's organizations.
 * There the table has 1 + 1 + N rows, `user` is NOT `localOnly`, and each touched
 * row enqueues its own op in `ps_crud`.
 *
 * The correct filter is NOT guessed: `ThemeProvider`
 * (`apps/web/src/providers/theme-provider.tsx`) seeds the guest row with
 * `db.insert(user).values({ id: uid, … })`, and for a guest tab `uid` is exactly
 * `VISITOR_ID`. Thus `eq(user.id, userId)` points to the SAME row that the
 * unfiltered branch intended to touch when the table had only one row —
 * identical behavior in the normal case, bounded in the rest. It is also the
 * form already used by `updateUserFontFamilyMutation` and
 * `updateUserImageMutation`, which never had a guest branch.
 *
 * Guarded by `packages/shared/src/users/__tests__/mutations-where.test.ts`.
 *
 * ── §4.11: `user` is NOT excluded from verified write protection ────────
 * (updated after the seeding guard of `ThemeProvider`, same day) ──────
 *
 * `infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1 audits 11 repos that excluded
 * `user` from the ghost `UPDATE` assuming that "the server already created the
 * row before the client writes, so it is always synchronized": across all
 * 11 the exclusion was WRONG, starting with the canon itself. It was verified
 * here with the 3 commands required by that section:
 *
 *   git grep -n '\.insert(user' -- '*.ts' '*.tsx' | grep -v __tests__
 *     → ONLY `apps/web/src/providers/theme-provider.tsx:158`.
 *   git grep -rn 'insert(user' apps/web/src/**\/theme* apps/web/src/**\/*provider*
 *     → the same site.
 *   git grep -n 'ensureUser\|ensureUserSettingsRow'
 *     → ZERO results: there is no transactional safety net.
 *
 * These two facts, on their own, already open the window without needing any
 * local insert:
 *
 *   - `db` (the PowerSync accessor) becomes non-null as soon as local SQLite
 *     SQLite is opened, not when the initial sync finishes.
 *   - `userId()` becomes active as soon as `signUpActor`/`signInActor` receive
 *     the server session (`packages/shared/src/auth/controllers.ts`) — an
 *     independent REST call from the PowerSync stream.
 *
 * Between the time `db`+`userId()` are ready and the initial checkpoint brings the
 * `user` row created by the server on registration, there is a window where
 * a normal `UPDATE` (language, theme…) lands against a row that the local view
 * does not yet have — matches ZERO rows and returns "success" silently.
 *
 * (Historical context, already CLOSED) Until the guard above, a third factor
 * worsened that same window for real accounts: the `createEffect` of
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) inserted
 * locally a 3-column ghost `user` row for ANY `uid` — its
 * guard was just `if (!db || !uid) return`, without distinguishing guest from real
 * account nor waiting for `status().hasSynced` — racing against the real row that the
 * server had already created (`packages/core-rust/src/db/user.rs:101,350`,
 * `INSERT INTO "user"`) and that was on its way via sync. The guard
 * `uid !== VISITOR_ID` of `theme-provider.tsx` closed that specific root cause.
 *
 * **This does NOT make the protection of `patchRowVerified` on `user` unnecessary**:
 * the two points above (`db` and `userId()` ready before the
 * checkpoint) still open the same window for any early write,
 * with or without a ghost row in the middle — it is generic defense in depth
 * of the checkpoint, not a patch attached to the already-closed bug. If one day
 * it is retired, let it be because it is proven that the window above no longer exists.
 */

/**
 * Shared Drizzle mutation for updating User locale.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY to the local row.
 *
 * It is written local-first (like language, font, and theme), not through REST:
 * the new photo appears instantly and survives a cold reload while offline. The
 * BINARY does go through REST (`POST /api/avatar`) because it does not fit in
 * the sync queue; only the key returned by that upload travels through here.
 *
 * `null` = "remove the photo" (the initials return).
 *
 * A guest has no syncable row of its own and cannot have an avatar (zero rule),
 * so there is no `VISITOR_ID` branch here as there is for `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}
